/**
 * useSupportSessionsLookups.ts
 * ----------------------------------------------------------------------------
 * Hooks de lookup leves usados pelos filtros das páginas de Sessões de Suporte
 * (/wl/sessions e /admin/sessions). Retornam apenas os campos mínimos para
 * popular dropdowns: tenant {id, name, slug} e operador {user_id, display_name, email}.
 *
 * Reutilizam RPCs já existentes (wl_list_tenants, wl_list_operators) — operadores
 * WL são globais (não pertencem a um tenant) e o root_admin tem bypass natural
 * nestas RPCs via wl_assert_permission.
 *
 * RELEASE: 2026-05-30 v1.0.0 - Versão inicial.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

export interface TenantLookupItem {
  id: string;
  name: string;
  slug: string | null;
}

export interface OperatorLookupItem {
  user_id: string;
  display_name: string | null;
  email: string | null;
}

/** Lista compacta de tenants para popular o filtro <Select>. */
export function useTenantsLookup(enabled = true) {
  return useQuery({
    queryKey: ["support-sessions-tenants-lookup"],
    queryFn: async (): Promise<TenantLookupItem[]> => {
      const { data, error } = await supabase.rpc("wl_list_tenants", {
        p_search: null,
        p_status: null,
        p_plan: null,
        p_limit: 500,
        p_offset: 0,
        p_created_from: null,
        p_created_to: null,
      });
      if (error) {
        logger.error("[useTenantsLookup]", error.message);
        throw error;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return ((data ?? []) as any[]).map((t) => ({
        id: t.id,
        name: t.name,
        slug: t.slug ?? null,
      }));
    },
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Lista compacta de operadores WL para popular o filtro <Select>. */
export function useOperatorsLookup(enabled = true) {
  return useQuery({
    queryKey: ["support-sessions-operators-lookup"],
    queryFn: async (): Promise<OperatorLookupItem[]> => {
      const { data, error } = await supabase.rpc("wl_list_operators");
      if (error) {
        logger.error("[useOperatorsLookup]", error.message);
        throw error;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return ((data ?? []) as any[])
        .map((r) => ({
          user_id: r.user_id,
          display_name: r.display_name ?? null,
          email: r.email ?? null,
        }))
        .filter((r) => !!r.user_id);
    },
    enabled,
    staleTime: 5 * 60_000,
  });
}
